/**
 * DecisionCardInlineView — Sub-Batch 3R (Niranjan, Feb 2026).
 *
 * Renders the BLUP + Consensus + ΔG sections from a trait's result
 * dict inline in the View Analysis screen. Mirrors what the
 * Breeder's Decision Card PDF produces — so the on-screen view and
 * the downloadable PDF stay in lockstep (Pattern 12: publication-ready
 * and field-ready outputs both).
 *
 * Consumes the per-trait result blocks shipped in Sub-Batches:
 *   • 3I (BLUP)        — `trait.blup_results`
 *   • 3J (Consensus)   — `trait.blup_results.consensus_classification`
 *   • 3K (ΔG)          — `trait.breeding_progress`
 *
 * Used by EnhancedResults.GenericAnalysisView for any design wired
 * to the Universal Selection Index (Augmented, RBD, Alpha-Lattice,
 * Split-Plot, Strip-Plot per Sub-Batches 3M-3P).
 */
import React from 'react';

const fmt = (v, digits = 4) => {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'number') {
    if (!Number.isFinite(v)) return String(v);
    return v.toFixed(digits);
  }
  return String(v);
};

const VERDICT_STYLES = {
  green: { border: '#16A34A', fill: '#E8F4ED', label: 'GREEN' },
  amber: { border: '#B45309', fill: '#FFF6E0', label: 'AMBER' },
  red: { border: '#B11F1F', fill: '#FDE7E7', label: 'RED' },
  high_gain_expected: { border: '#16A34A', fill: '#E8F4ED', label: 'HIGH GAIN' },
  moderate_gain_expected: { border: '#B45309', fill: '#FFF6E0', label: 'MODERATE GAIN' },
  low_gain_expected: { border: '#B11F1F', fill: '#FDE7E7', label: 'LOW GAIN' },
  no_gain_possible: { border: '#7A1717', fill: '#FDE7E7', label: 'NO GAIN' },
};

function VerdictBanner({ verdict, copy, testId }) {
  const v = VERDICT_STYLES[verdict] || VERDICT_STYLES.amber;
  return (
    <div
      className="rounded-lg p-3 my-3 flex items-start gap-3"
      style={{
        background: v.fill,
        border: `2px solid ${v.border}`,
      }}
      data-testid={testId}
    >
      <div
        className="text-white font-bold rounded px-3 py-1 text-xs whitespace-nowrap"
        style={{ background: v.border, minWidth: 80, textAlign: 'center' }}
      >
        {v.label}
      </div>
      <p className="text-sm text-[#2C2A28] leading-relaxed flex-1">{copy}</p>
    </div>
  );
}

function MiniTable({ title, headers, rows, testId, accentColor = '#2D6A4F' }) {
  if (!rows || !rows.length) return null;
  return (
    <div className="overflow-x-auto mt-3" data-testid={testId}>
      <h5 className="font-semibold text-[#2C2A28] mb-2 text-xs uppercase tracking-wider">{title}</h5>
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr style={{ background: accentColor }} className="text-white">
            {headers.map(h => (
              <th key={h} className="px-2 py-1.5 text-center font-semibold border border-[#1B4332]">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri} className={ri % 2 === 0 ? 'bg-white' : 'bg-[#F0FAF0]'}>
              {row.map((cell, ci) => (
                <td key={ci} className="px-2 py-1 text-center font-mono border border-[#E8E6E1]">{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MiniKV({ title, entries, testId }) {
  if (!entries || !entries.length) return null;
  return (
    <div className="overflow-x-auto mt-3" data-testid={testId}>
      <h5 className="font-semibold text-[#2C2A28] mb-2 text-xs uppercase tracking-wider">{title}</h5>
      <table className="w-full text-xs border-collapse">
        <tbody>
          {entries.map(([k, v]) => (
            <tr key={k} className="border-b border-[#E8E6E1]">
              <td className="px-3 py-1.5 font-semibold text-[#2C2A28] bg-[#F4F2EC]" style={{ width: '40%' }}>{k}</td>
              <td className="px-3 py-1.5 text-right font-mono text-[#5E5A56]">{v}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function DecisionCardInlineView({ blup, breedingProgress, traitName }) {
  const hasBlup = blup && typeof blup === 'object';
  const hasBlupOk = hasBlup && !blup.error && blup.converged;
  const hasBp = breedingProgress && typeof breedingProgress === 'object' && !breedingProgress.error;

  if (!hasBlup) return null;

  // ── Error envelope card (BLUP failed) — Pattern 3 transparency
  if (hasBlup && (blup.error || blup.converged === false)) {
    return (
      <div className="mt-4 rounded-xl border-2 border-amber-300 bg-amber-50 p-4" data-testid={`decision-card-error-${traitName}`}>
        <h4 className="font-semibold text-amber-900 mb-2 text-sm">⚠ BLUP / Decision Card Unavailable</h4>
        <p className="text-xs text-amber-800 italic">
          {blup.error || 'REML mixed-model fit did not converge for this trait.'}
        </p>
        <p className="text-xs text-amber-700 mt-2">
          Falling back to BLUE-based ranking shown above. References: Henderson 1950, Piepho et al. 2008.
        </p>
      </div>
    );
  }

  if (!hasBlupOk) return null;

  const cc = blup.consensus_classification || {};
  const predictions = blup.predictions || [];
  const recommended = cc.recommended_selection_set || [];
  const perEntry = cc.per_entry || [];
  const amberEntries = perEntry.filter(d => d.verdict === 'amber');

  // BLUP variance components + diagnostics KV
  const blupDiagEntries = [
    ['Model', blup.model_formula || '—'],
    ['Estimation', blup.method || '—'],
    ['σ²g (genetic, REML)', fmt(blup.sigma2_g)],
    ['σ²e (residual, REML)', fmt(blup.sigma2_e_blup)],
    ['H² (entry-mean, BLUP basis)', fmt(blup.H2_blup_entry_mean)],
    ['REML log-likelihood', fmt(blup.reml_loglikelihood)],
    [
      `Spearman ρ (BLUE vs BLUP, top-${cc.top_k || 10})`,
      `${fmt(cc.spearman_rho ?? blup.rank_correlation?.spearman_rho)}  (${cc.intersection_count ?? blup.rank_correlation?.top_n_intersection ?? '?'}/${cc.top_k ?? blup.rank_correlation?.top_n ?? '?'} = ${fmt(cc.intersection_pct ?? blup.rank_correlation?.top_n_intersection_pct, 1)}%)`,
    ],
  ];

  // BLUE vs BLUP predictions (top 20)
  const MAX_PREDS = 20;
  const predRows = predictions.slice(0, MAX_PREDS).map(p => [
    p.BLUP_Rank,
    p.Entry,
    p.EntryType,
    p.n_obs,
    fmt(p.BLUE, 2),
    fmt(p.BLUP, 2),
    fmt(p.Shrinkage_toward_mean, 2),
    p.BLUE_Rank,
  ]);

  // Recommended Selection Set rows
  const recRows = recommended.slice(0, 10).map((r, i) => [
    i + 1,
    r.Entry,
    r.EntryType,
    fmt(r.BLUE, 2),
    fmt(r.BLUP, 2),
    r.BLUE_Rank,
    r.BLUP_Rank,
  ]);

  // ΔG per fraction rows
  const bpFractionRows = (hasBp && Array.isArray(breedingProgress.gain_per_fraction))
    ? breedingProgress.gain_per_fraction.map(r => [
        `top ${r.selection_fraction_pct}%`,
        fmt(r.selection_intensity, 4),
        fmt(r.delta_G_per_cycle, 4),
        r.delta_G_per_cycle_pct_of_mean != null ? `${fmt(r.delta_G_per_cycle_pct_of_mean, 2)}%` : '—',
      ])
    : [];

  // ΔG inputs KV
  const bpInputs = (hasBp && breedingProgress.inputs) || {};
  const bpInputEntries = [
    ['H² (entry-mean, BLUP basis)', fmt(bpInputs.h2_entry_mean)],
    ['σ_g (genetic SD)', fmt(bpInputs.sigma_g)],
    ['σ_e (residual SD)', fmt(bpInputs.sigma_e)],
    ['σ_P (phenotypic SD)', fmt(bpInputs.sigma_p)],
    ['Grand mean', fmt(bpInputs.grand_mean)],
  ];

  // Cumulative projection
  const cumDG = (hasBp && breedingProgress.cumulative_projection?.cumulative_dG) || [];
  const cycles_to_show = Math.min(5, cumDG.length);
  const cumRows = cycles_to_show > 0
    ? [['Cumulative ΔG', ...cumDG.slice(0, cycles_to_show).map(v => fmt(v, 2))]]
    : [];
  const cumHeaders = cycles_to_show > 0
    ? ['Cycle', ...Array.from({ length: cycles_to_show }, (_, i) => String(i + 1))]
    : [];

  return (
    <div className="mt-6 rounded-xl border-2 border-[#2D6A4F] bg-[#F9FCF9] p-4" data-testid={`decision-card-inline-${traitName}`}>
      <h4 className="font-semibold text-[#2C2A28] mb-3 text-sm uppercase tracking-wider">
        Breeder's Decision Card — Inline View
      </h4>

      {/* ── 1. BLUP Companion ───────────────────────────────────────── */}
      <div className="mb-4">
        <h5 className="font-semibold text-[#2D6A4F] mb-2 text-xs uppercase tracking-wider">
          1. BLUP Companion ({blup.design_label || 'Mixed Model'})
        </h5>
        <MiniKV title="BLUP Diagnostics" entries={blupDiagEntries} testId={`blup-diag-${traitName}`} />
        {blup.sigma2_g_at_boundary && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-2 mt-2 text-xs italic text-amber-800" data-testid={`sigma2g-boundary-${traitName}`}>
            ⚠ σ²g pinned at the boundary (≈ 0). BLUPs collapse toward the grand mean and BLUE-based rankings remain the appropriate selection basis for this trait.
          </div>
        )}
        <MiniTable
          title={`BLUE vs BLUP Predictions${predictions.length > MAX_PREDS ? ` (top ${MAX_PREDS} of ${predictions.length})` : ''}`}
          headers={['Rank (BLUP)', 'Entry', 'Type', 'n_obs', 'BLUE', 'BLUP', 'Shrinkage (BLUE − BLUP)', 'Rank (BLUE)']}
          rows={predRows}
          testId={`blue-blup-preds-${traitName}`}
        />
        {blup.interpretation && (
          <p className="text-xs italic text-[#5E5A56] mt-2">{blup.interpretation}</p>
        )}
      </div>

      {/* ── 2. BLUE/BLUP Selection Consensus ───────────────────────── */}
      {cc.aggregate_verdict && (
        <div className="mb-4">
          <h5 className="font-semibold text-[#2D6A4F] mb-2 text-xs uppercase tracking-wider">
            2. BLUE/BLUP Selection Consensus
          </h5>
          <VerdictBanner
            verdict={cc.aggregate_verdict}
            copy={cc.aggregate_copy}
            testId={`consensus-verdict-${traitName}`}
          />
          {recRows.length > 0 && (
            <MiniTable
              title={`Recommended Selection Set (BLUE ∩ BLUP top-${cc.top_k || 10})`}
              headers={['Avg Rank', 'Entry', 'Type', 'BLUE', 'BLUP', 'BLUE Rank', 'BLUP Rank']}
              rows={recRows}
              testId={`recommended-set-${traitName}`}
            />
          )}
          {amberEntries.length > 0 && (
            <MiniTable
              title="Method-Sensitive Entries (AMBER)"
              headers={['Entry', 'Type', 'BLUE Rank', 'BLUP Rank', 'In top-K of', 'Verdict']}
              rows={amberEntries.map(d => [
                d.Entry,
                d.EntryType,
                d.BLUE_Rank,
                d.BLUP_Rank,
                d.in_top_k_BLUE ? 'BLUE only' : 'BLUP only',
                'AMBER (method-sensitive)',
              ])}
              testId={`amber-entries-${traitName}`}
              accentColor="#B45309"
            />
          )}
        </div>
      )}

      {/* ── 3. Predicted Genetic Gain ──────────────────────────────── */}
      {hasBp && (
        <div className="mb-4">
          <h5 className="font-semibold text-[#2D6A4F] mb-2 text-xs uppercase tracking-wider">
            3. Predicted Genetic Gain (ΔG) — Breeders' Equation
          </h5>
          <VerdictBanner
            verdict={breedingProgress.verdict}
            copy={breedingProgress.interpretation}
            testId={`bp-verdict-${traitName}`}
          />
          <div className="grid md:grid-cols-2 gap-4 mt-3">
            <MiniTable
              title="ΔG per Cycle"
              headers={['Selection', 'Intensity i', 'ΔG per cycle', '% of mean']}
              rows={bpFractionRows}
              testId={`bp-fractions-${traitName}`}
            />
            <MiniKV title="ΔG Inputs" entries={bpInputEntries} testId={`bp-inputs-${traitName}`} />
          </div>
          {cumRows.length > 0 && (
            <>
              <MiniTable
                title={`Cumulative ΔG Projection (top ${breedingProgress.cumulative_projection?.selection_fraction_pct || 10}%, ${cycles_to_show} cycles)`}
                headers={cumHeaders}
                rows={cumRows}
                testId={`bp-cumulative-${traitName}`}
              />
              {breedingProgress.cumulative_projection?.caveat && (
                <p className="text-[10px] italic text-[#7A6A56] mt-1">
                  {breedingProgress.cumulative_projection.caveat}
                </p>
              )}
            </>
          )}
        </div>
      )}

      {/* ── 4. Citations footer ─────────────────────────────────────── */}
      <div className="border-t border-[#D5D1CC] pt-2 mt-3">
        <p className="text-[10px] italic text-[#5E5A56] leading-snug">
          Methods: Henderson (1950) BLUP via REML mixed model; Piepho et al. (2008) BLUE/BLUP selection;
          Pattern 9 intersection rule for consensus (≥80% GREEN, 50–79% AMBER, &lt;50% RED);
          Falconer &amp; Mackay (1996, §11.1) Breeders' Equation ΔG = i × √H² × σ_P
          with Becker (1992 App. A) truncation intensities. BKAgriD — Sub-Batches 3I/3J/3K/3M/3N/3R.
        </p>
      </div>
    </div>
  );
}

export default DecisionCardInlineView;
